import { CurrencyPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { VtmnPlayIconButtonVariantEnum } from '~/component/buttons/vtmn-play-icon-button/enum/vtmn-play-icon-button-variant.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { ModalComponent } from '~/component/modal/modal.component';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { Activity } from '~/core/model/activity.model';
import { DisplayAgeRangePipe } from '~/core/pipes/display-age-range.pipe';
import { DisplayLevelPipe } from '~/core/pipes/display-level.pipe';
import { DisplayPricePipe } from '~/core/pipes/display-price.pipe';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { SportsService } from '~/core/services/sports/sports.service';
import { LocaleService } from '~/i18n/services/locale.service';
import { EditionLinkInterface } from '~/organizer/activity-edition/interfaces/edition-link.interface';
import {
  checkActivityMandatoryFields,
  getActivityTypeForMandatoryFields,
} from '~/organizer/shared/helpers/activity/checkActivityMandatoryFields/check-activity-mandatory-fields.helper';
import { activityMandatoryFieldsDefaultValue } from '~/toggles/default-values/organizer/activity/by-type-mandatory-fields';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';

@Component({
  selector: 'app-organizer-activity-edition-layout',
  templateUrl: './edition.component.html',
})
export class OrganizerActivityEditionComponent implements OnInit, OnDestroy {
  @ViewChild('confirmationModal') public confirmationModal: ModalComponent;
  private readonly defaultMandatoryFieldsVariant =
    activityMandatoryFieldsDefaultValue;
  private readonly subscriptions = new Subscription();

  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly tipsVariant = TipsVariantEnum;
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayIconButtonVariant = VtmnPlayIconButtonVariantEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;

  public activity: Activity;
  public activityDetailsLinks: EditionLinkInterface[];
  public activityParametersLinks: EditionLinkInterface[];
  public activityTypologyLinks: EditionLinkInterface[];
  public activityFormulesLinks: EditionLinkInterface[] = [];
  public isTabletDevice: boolean;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly matchMediaService: MatchMediaService,
    private readonly sportsService: SportsService,
    private readonly translateService: TranslateService,
    private readonly localeService: LocaleService,
    private readonly displayAgePipe: DisplayAgeRangePipe,
    private readonly displayLevelPipe: DisplayLevelPipe,
    private readonly paramethorService: ParamethorService,
    private readonly currencyPipe: CurrencyPipe
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.activatedRoute.data.subscribe(
        ({ activityData }) => (this.activity = activityData.activity)
      )
    );

    const ACTIVITY_TYPE = getActivityTypeForMandatoryFields(
      this.activity,
      this.defaultMandatoryFieldsVariant
    );

    this.subscriptions.add(this.matchMediaService.isMobileDevice().subscribe());

    this.activityTypologyLinks = [
      {
        text: this.translateService.instant(
          'organizer.edit_activity.listing_typology.sport.label'
        ),
        data: this.sport,
        path: this.localizeRouterService
          .translateRoute('./organizer.activity-edition.sport')
          .toString(),
        isOptional:
          !this.defaultMandatoryFieldsVariant[ACTIVITY_TYPE].includes('sport'),
      },
      {
        isDisabled:
          ['BIRTHDAY'].includes(this.activity.type) ||
          ['PRODUCT_TEST'].includes(this.activity.type),
        text: this.translateService.instant(
          'organizer.edit_activity.listing_typology.target.label'
        ),
        data: this.buildAudience(),
        path: this.localizeRouterService
          .translateRoute('./organizer.activity-edition.audience')
          .toString(),
        isOptional:
          !this.defaultMandatoryFieldsVariant[ACTIVITY_TYPE].includes(
            'typicalAgeRange'
          ),
      },
      {
        isDisabled: ['BIRTHDAY'].includes(this.activity.type),
        text: this.translateService.instant(
          'organizer.edit_activity.listing_typology.duration.label'
        ),
        data: this.activity.formattedDuration(this.localeService.dateLocale),
        path: this.localizeRouterService
          .translateRoute('./organizer.activity-edition.duration')
          .toString(),
        isOptional:
          !this.defaultMandatoryFieldsVariant[ACTIVITY_TYPE].includes(
            'duration'
          ),
      },
    ];

    this.activityDetailsLinks = [
      {
        isDisabled: ['LIVE'].includes(this.activity.type),
        text: this.translateService.instant(
          'organizer.edit_activity.listing_details.location.label'
        ),
        data: this.activity.fullAddress,
        path: this.localizeRouterService
          .translateRoute('./organizer.activity-edition.address')
          .toString(),
        isOptional:
          !this.defaultMandatoryFieldsVariant[ACTIVITY_TYPE].includes(
            'location'
          ),
      },
      {
        text: this.translateService.instant(
          'organizer.edit_activity.listing_details.equipment_required.label'
        ),
        data: this.activity.requiredEquipment?.join(', '),
        path: this.localizeRouterService
          .translateRoute('./organizer.activity-edition.equipment-required')
          .toString(),
        isOptional:
          !this.defaultMandatoryFieldsVariant[ACTIVITY_TYPE].includes(
            'requiredEquipment'
          ),
      },
      {
        isDisabled: ['LIVE'].includes(this.activity.type),
        text: this.translateService.instant(
          'organizer.edit_activity.listing_details.equipment_provided.label'
        ),
        data: this.activity.providedEquipment?.join(', '),
        path: this.localizeRouterService
          .translateRoute('./organizer.activity-edition.equipment-provided')
          .toString(),
        isOptional:
          !this.defaultMandatoryFieldsVariant[ACTIVITY_TYPE].includes(
            'providedEquipment'
          ),
      },
      {
        text: this.translateService.instant(
          'organizer.edit_activity.listing_details.activity_description.label'
        ),
        data: this.activity.description,
        path: this.localizeRouterService
          .translateRoute('./organizer.activity-edition.description')
          .toString(),
        isOptional:
          !this.defaultMandatoryFieldsVariant[ACTIVITY_TYPE].includes(
            'description'
          ),
      },
      {
        text: this.translateService.instant(
          'organizer.edit_activity.listing_details.activity_title.label'
        ),
        data: this.activity.name,
        path: this.localizeRouterService
          .translateRoute('./organizer.activity-edition.title')
          .toString(),
        isOptional:
          !this.defaultMandatoryFieldsVariant[ACTIVITY_TYPE].includes('name'),
      },
      {
        text: this.translateService.instant(
          'organizer.edit_activity.listing_details.photo.label'
        ),
        data: this.activity.image?.url
          ? this.translateService.instant(
              'organizer.edit_activity.draft.main_media_uploaded'
            )
          : null,
        path: this.localizeRouterService
          .translateRoute('./organizer.activity-edition.photo')
          .toString(),
        isOptional:
          !this.defaultMandatoryFieldsVariant[ACTIVITY_TYPE].includes('image'),
      },
    ];

    this.activityParametersLinks = [
      {
        text: this.translateService.instant(
          'organizer.edit_activity.listing_parameters.upload.label'
        ),
        data: this.activity.bookingAttachments
          ?.map((attachment) => attachment.name)
          .join(', '),
        path: this.localizeRouterService
          .translateRoute('./organizer.activity-edition.uploads')
          .toString(),
        isOptional:
          !this.defaultMandatoryFieldsVariant[ACTIVITY_TYPE].includes(
            'uploads'
          ),
      },
    ];

    this.formuleListInit();
  }

  private formuleListInit(): void {
    const displayPricePipe = new DisplayPricePipe(
      this.currencyPipe,
      this.translateService
    );

    this.activity?.indicativeOffers.forEach((formule) => {
      const price = displayPricePipe.transform(formule.price, formule.currency);
      this.activityFormulesLinks.push({
        data: formule.description,
        isOptional: false,
        path: this.localizeRouterService
          .translateRoute(
            './organizer.activity-edition.formules/' + formule.identifier
          )
          .toString(),
        text: `${formule.name} - ${price}`,
      });
    });
  }

  public get isBirthday(): boolean {
    return (
      ['BIRTHDAY'].includes(this.activity.type) ||
      ['PRODUCT_TEST'].includes(this.activity.type)
    );
  }

  public get backLink(): BackLinkInterface {
    return {
      path: this.localizeRouterService
        .translateRoute(`/organizer.activity.manage/${this.activity?.sku}`)
        .toString(),
    };
  }

  public get displayProficiencyLevel(): boolean {
    return !!this.activity?.proficiencyLevel;
  }

  public get hasHeader(): boolean {
    return !this.isTabletDevice;
  }

  public get haveIncompleteFields(): boolean {
    const mandatoryFieldsVariant = this.paramethorService.getObject(
      ParamethorNameEnum.OrganizerSharedGuardActivityByTypeMandatoryFields,
      this.defaultMandatoryFieldsVariant
    );

    return checkActivityMandatoryFields(this.activity, mandatoryFieldsVariant);
  }

  public get sport(): string {
    return this.sportsService.getSport(this.activity.sports?.[0])?.name;
  }

  private buildAudience(): string {
    const ageRange = this.activity.typicalAgeRange
      ? `${this.displayAgePipe.transform(this.activity.typicalAgeRange)}`
      : '';
    const proficiencyLevel = this.displayProficiencyLevel
      ? `${this.displayLevelPipe.transform(this.activity.proficiencyLevel)}`
      : '';

    if (ageRange && proficiencyLevel) {
      return `${ageRange} - ${proficiencyLevel}`;
    }
    return ageRange || proficiencyLevel;
  }
}
